import BreadCrump from '../tools/breadcrump';
import * as React from 'react';
import { config } from "../../data/config";
import { useEffect, useState } from "react";
import ImageUploader from '../tools/uploaderImage';
import axios from 'axios';
import { getCookies } from "cookies-next";
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import HeaderOfPage from '../tools/headerOfPage';
import { Checkbox, CheckboxGroup } from 'rsuite';
import ArrowUpLine from '@rsuite/icons/esm/react/ArrowUpLine';
import ArrowDownLine from '@rsuite/icons/esm/react/ArrowDownLine';

type LanguageCode = 'ku' | 'fa' | 'en' | 'ar' | 'tr';

interface MultilingualField {
    ku: string;
    fa: string;
    en: string;
    ar: string;
    tr: string;
    [key: string]: string;
}
interface LanguageData {
    id: String;
    code: String;
    label: String;
    defaultlang: Boolean;
    arranged: Number;
}

export default function LanguageSetting() {
    const { t } = useTranslation();
    const cookie = getCookies();
    const [edit, setEdit] = useState<LanguageData>();
    const [data, setData] = useState<LanguageCode[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [editshow, setEditShow] = useState(false);
    const [adminMode, setAdminMode] = useState<'marketing' | 'public'>('marketing');
    const [activeLang, setActiveLang] = useState<LanguageCode>('en');
    const languages = [
        { code: 'ku' as LanguageCode, label: 'Kurdish' },
        { code: 'fa' as LanguageCode, label: 'فارسی' },
        { code: 'en' as LanguageCode, label: 'English' },
        { code: 'ar' as LanguageCode, label: 'العربية' },
        { code: 'tr' as LanguageCode, label: 'Türkçe' }
    ];


    // Helper function to safely access multilingual fields
    const getMultilingualValue = (field: MultilingualField | string | undefined, lang: LanguageCode): string => {
        if (!field) return '';
        if (typeof field === 'string') return field;
        if (typeof field === 'object') {
            return field[lang] || '';
        }
        return '';
    };

    const breadcrump = [
        { name: 'Dashboard', url: '/dashboard' },
        { name: 'Setting', url: '/dashboard/setting' },
        { name: 'General', url: null },
    ];

    // Load settings
    const load = async () => {
        try {
            const res = await axios.get(
                `${config.url}/v1/dashboard/langsetting`,
                { headers: { Authorization: 'Bearer ' + cookie['token'] } }
            );
            const message = res.data.message || {};
            // Ensure multilingual structure exists

            setData(message);

        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: t('Error loading settings')
            });
        } finally {
            setLoading(false);
        }
    };
    const deleted = async (id: any) => {
        Swal.fire({
            title: t('Are you sure?'),
            text: t('Delete this language!'),
            icon: 'warning',
            showCancelButton: true,
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await axios.delete(`${config.url}/v1/dashboard/langsetting/${id}`, { headers: { Authorization: 'Bearer ' + cookie['token'] } });
                } catch (error) {
                    Swal.fire({
                        icon: 'error',
                        title: t('Error deleting language')
                    });
                }
            }
            load();
        });

    };

    const edited = async (id: any) => {
        const item = (data as any[]).find((item: any) => item.id === id);
        if (item) {
            setEdit(item as LanguageData);
            setEditShow(true);
        } else {
            setEdit(undefined);
        }
    };

    // Save settings
    const save = async () => {
        if (!data) return;

        setSaving(true);
        setErrors({});

        try {
            if (edit!.id) {
                await axios.put(
                    `${config.url}/v1/dashboard/langsetting/${edit?.id}`,
                    edit,
                    { headers: { Authorization: 'Bearer ' + cookie['token'] } }
                );
                setEditShow(false);
                load();
                Swal.fire({
                    title: t('ذخیره شد'),
                    icon: 'success',
                    timer: 1500,
                    showConfirmButton: false
                });
            } else {
                await axios.post(
                    `${config.url}/v1/dashboard/langsetting`,
                    edit,
                    { headers: { Authorization: 'Bearer ' + cookie['token'] } }
                );
                setEditShow(false);
                load();
                Swal.fire({
                    title: t('ذخیره شد'),
                    icon: 'success',
                    timer: 1500,
                    showConfirmButton: false
                });
            }

        } catch (error: any) {
            const err = error.response?.data;
            if (err?.errors) {
                // Handle validation errors
                const fieldErrors: Record<string, string> = {};
                Object.keys(err.errors).forEach(key => {
                    fieldErrors[key] = err.errors[key].message;
                });
                setErrors(fieldErrors);
                Swal.fire({
                    icon: 'warning',
                    title: t('Please correct invalid fields')
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: t('Error saving')
                });
            }
        } finally {
            setSaving(false);
        }
    };
    const orderup = async (id: any) => {
        try {
            await axios.put(`${config.url}/v1/dashboard/langsetting/orderup/${id}`, { headers: { Authorization: 'Bearer ' + cookie['token'] } });
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: t('Error ordering language')
            });
        }
    };

    const orderdown = async (id: any) => {
        try {
            await axios.put(`${config.url}/v1/dashboard/langsetting/orderdown/${id}`, { headers: { Authorization: 'Bearer ' + cookie['token'] } });
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: t('Error ordering language')
            });
        }
    };
    useEffect(() => {
        load();
    }, []);

    const inputStyle = "w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition";

    if (loading) {
        return (
            <div className="bg-slate-100 p-4 m-4 min-h-[100vh]">
                <div className="text-center py-10 text-gray-500">{t('در حال بارگذاری...')}</div>
            </div>
        );
    }

    return (
        <div className="bg-slate-100">
            <div className="p-4 m-4 min-h-[100vh]">
                <BreadCrump data={breadcrump} />
                <HeaderOfPage name={t('تنظیمات زبان')} />


                <div className="grid grid-cols-1 lg:grid-cols-1 gap-6" dir="rtl">
                    {/* Form */}
                    <div className="bg-white p-6 rounded-xl shadow">
                        {/* Save Button (Top) */}
                        <div className="mb-6 flex justify-end">
                            {
                                editshow == true && <button
                                    onClick={save}
                                    disabled={saving}
                                    className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
                                >
                                    {saving ? t('در حال ذخیره...') : t('ذخیره تغییرات')}
                                </button>
                            }

                            <button onClick={(e) => { setEditShow(!editshow) }} className='px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition mr-4 ml-3'>
                                {editshow == false ? t('New') : t('Cancel')}
                            </button>
                        </div>
                        {editshow == false ?
                            <div>
                                <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                                {t('Name')}
                                            </th>
                                            <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                                {t('Code')}
                                            </th>
                                            <th scope="col" className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                                {t('ترتیب')}
                                            </th>
                                            <th scope="col" className="px-6 w-[40px] py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                                {t('Action')}
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {
                                            data.map((item: any) => {
                                                return (
                                                    <tr className={`${edit?.id == item.id ? 'bg-gray-100' : ''} hover:bg-gray-100 cursor-pointer border border-gray-200 rounded-lg transition duration-300 ease-in-out h-[50px]`}>
                                                        <td className='px-2 py-2'>
                                                            {item.label}
                                                        </td>
                                                        <td className='px-2 py-2'>{item.code}</td>
                                                        <td>
                                                            <div className='flex w-full justify-center gap-2'>
                                                                <div onClick={() => { orderup(item.id) }} className='cursor-pointer text-blue-600' >
                                                                    <ArrowUpLine></ArrowUpLine>
                                                                </div>
                                                                <div onClick={() => { orderdown(item.id) }}>
                                                                    <ArrowDownLine></ArrowDownLine>
                                                                </div>
                                                            </div>

                                                        </td>
                                                        <td>
                                                            <div className='flex w-full justify-around'>
                                                                <div onClick={() => { edited(item.id) }}>
                                                                    <img src="/icons/editgreen.svg" width={20}></img>
                                                                </div>
                                                                <div onClick={() => { deleted(item.id) }}>
                                                                    <img src="/icons/delred.svg" width={20}></img>

                                                                </div>
                                                            </div>
                                                        </td>

                                                    </tr>
                                                )
                                            })
                                        }

                                    </tbody>

                                </table>


                            </div>
                            :
                            <form className="space-y-6">
                                <div>
                                    <label className="block text-right text-sm font-medium text-gray-700 mb-2">
                                        {t('Slug')}
                                    </label>
                                    <input
                                        type="text"
                                        onChange={(e) => {
                                            setEdit((prev: any) => ({
                                                ...prev,
                                                code: e.target.value
                                            }))
                                        }}
                                        defaultValue={edit?.code == undefined ? '' : edit.code as string}
                                        className={`${inputStyle} ${errors.code ? 'border-red-500 bg-red-50' : ''}`}
                                        placeholder={t('نام زبان را وارد کنید')}
                                    />
                                    {errors.name && <p className="mt-1 text-sm text-red-600">{errors.lang}</p>}
                                </div>
                                <div>
                                    <label className="block text-right text-sm font-medium text-gray-700 mb-2">
                                        {t('نام زبان ')}
                                    </label>
                                    <input
                                        type="text"
                                        onChange={(e) => {
                                            setEdit((prev: any) => ({
                                                ...prev,
                                                label: e.target.value
                                            }))
                                        }}
                                        defaultValue={edit?.label == undefined ? '' : edit.label as string}

                                        className={`${inputStyle} ${errors.label ? 'border-red-500 bg-red-50' : ''}`}
                                        placeholder={t('نام زبان را وارد کنید')}
                                    />
                                    {errors.lang && <p className="mt-1 text-sm text-red-600">{errors.label}</p>}
                                </div>

                                <div className={`flex items-center justify-around w-full`}>
                                    <label className=" text-right text-sm font-medium text-gray-700 ">
                                        {t('زبان پیش فرض')}
                                    </label>
                                    <input
                                        type="checkbox"
                                        onChange={(e) => {
                                            setEdit((prev: any) => (
                                                {
                                                    ...prev,
                                                    defaultlang: e.target.checked
                                                }
                                            ))
                                        }}
                                        defaultChecked={edit?.defaultlang == undefined ? false : edit.defaultlang as boolean}

                                        className={`${inputStyle} ${errors.name ? ' checkbox ' : ''}  w-5 h-5 rounded`}
                                        placeholder={t('نام زبان را وارد کنید')}
                                    />
                                    {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                                </div>
                                <div>
                                    <input onClick={(e) => { save() }} type='button'
                                        className={` px-4 py-2 rounded border border-blue-600 text-blue-600 hover:bg-blue-600 bg-blue-200 hover:text-white`} value={t('Save')}></input>
                                </div>
                            </form>
                        }
                    </div>

                    {/* Logo Upload */}

                </div>


            </div>
        </div>
    );
}